import { defineStore } from "pinia"
import { ref, computed } from "vue"
import type {
  Restaurant,
  RestaurantStats,
  Order,
  Menu,
  Table,
  CreateMenuRequest,
  CreateSectionRequest,
  CreateProductRequest,
} from "@/types"
import { restaurantService } from "@/services/restaurant"
import { authService } from "@/services/auth"
import { useAuthStore } from "./auth"
import { webSocketService } from "@/services/websocket"

export const useRestaurantStore = defineStore("restaurant", () => {
  const restaurant = ref<Restaurant | null>(null)
  const restaurants = ref<Restaurant[]>([])
  const allOrders = ref<Order[]>([])
  const stats = ref<RestaurantStats | null>(null)
  const orders = ref<Order[]>([])
  const activeOrders = ref<Order[]>([])
  const ordersPagination = ref({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  })
  const menus = ref<Menu[]>([])
  const tables = ref<Table[]>([])
  const tableQrStats = ref<Record<string, { totalScans: number; last30DaysScans: number }>>({})
  const carts = ref<any[]>([])
  const isLoading = ref(false)
  const isOrdersLoading = ref(false)
  const isActiveOrdersLoading = ref(false)

  const completedOrders = computed(() =>
    orders.value.filter(order => ["completed", "cancelled"].includes(order.status))
  )

  const fetchRestaurant = async (restaurantId: string) => {
    isLoading.value = true
    try {
      restaurant.value = await restaurantService.getRestaurant(restaurantId)
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchAllRestaurants = async () => {
    isLoading.value = true
    try {
      restaurants.value = await restaurantService.getAllRestaurants()
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchAllRestaurantsAdmin = async () => {
    isLoading.value = true
    try {
      restaurants.value = await restaurantService.getAllRestaurantsAdmin()
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchAllOrders = async () => {
    isOrdersLoading.value = true
    try {
      allOrders.value = await restaurantService.getAllOrders()
    } catch (error) {
      throw error
    } finally {
      isOrdersLoading.value = false
    }
  }

  const setActiveRestaurant = async (selectedRestaurant: Restaurant) => {
    // Wyczyść dane poprzedniej restauracji
    clearRestaurantData()

    // Ustaw nową restaurację
    restaurant.value = selectedRestaurant

    // Zapisz wybór restauracji w profilu użytkownika
    // Tylko dla restaurant_owner, admin i superadmin (nie dla customer/anonymous)
    const authStore = useAuthStore()
    const allowedRoles = ["restaurant_owner", "admin", "superadmin"]
    if (
      authStore.isAuthenticated &&
      !authStore.user?.isAnonymous &&
      authStore.user?.role &&
      allowedRoles.includes(authStore.user.role) &&
      selectedRestaurant.id
    ) {
      try {
        await authService.updateProfile({
          preferredRestaurantId: selectedRestaurant.id,
        })
      } catch (error) {
        console.error("Error saving restaurant preference:", error)
        // Nie przerywamy procesu jeśli zapisanie preferencji się nie powiodło
      }
    }

    // Przeładuj minimalny zestaw danych dla nowej restauracji
    // Globalnie ładujemy tylko aktywne zamówienia, a resztę (stoliki, menu, historia)
    // dociągają poszczególne widoki.
    if (selectedRestaurant.id) {
      try {
        await fetchActiveOrders(selectedRestaurant.id)

        // Ponownie połącz z websocketem dla nowej restauracji
        webSocketService.disconnect()
        webSocketService.connect()
      } catch (error) {
        console.error("Error loading restaurant data:", error)
      }
    }
  }

  const clearRestaurantData = () => {
    // Wyczyść wszystkie dane związane z restauracją
    orders.value = []
    activeOrders.value = []
    menus.value = []
    tables.value = []
    carts.value = []
    stats.value = null
    ordersPagination.value = {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0,
    }
  }

  const initializeUserRestaurant = async () => {
    if (!restaurant.value) {
      try {
        // Pobierz wszystkie restauracje
        await fetchAllRestaurants()

        // Sprawdź czy użytkownik ma preferowaną restaurację w profilu
        const authStore = useAuthStore()
        const allowedRoles = ["restaurant_owner", "admin", "superadmin"]
        const userRole = authStore.user?.role

        if (userRole && allowedRoles.includes(userRole) && restaurants.value.length > 0) {
          const preferredRestaurantId = authStore.user?.preferredRestaurantId

          if (preferredRestaurantId) {
            // Znajdź preferowaną restaurację w liście
            const preferredRestaurant = restaurants.value.find(r => r.id === preferredRestaurantId)

            if (preferredRestaurant) {
              await setActiveRestaurant(preferredRestaurant)
              console.log("Using preferred restaurant:", preferredRestaurant.name)
            } else {
              // Jeśli preferowana restauracja nie istnieje, użyj pierwszej dostępnej
              const firstRestaurant = restaurants.value[0]
              if (firstRestaurant) {
                await setActiveRestaurant(firstRestaurant)
                console.log(
                  "Preferred restaurant not found, using first available:",
                  firstRestaurant.name
                )
              }
            }
          } else {
            // Jeśli nie ma preferowanej, użyj pierwszej dostępnej
            const firstRestaurant = restaurants.value[0]
            if (firstRestaurant) {
              await setActiveRestaurant(firstRestaurant)
              console.log("No preferred restaurant, using first available:", firstRestaurant.name)
            }
          }
        }
      } catch (error) {
        console.error("Error initializing user restaurant:", error)
      }
    }
  }

  const fetchStats = async (_restaurantId: string) => {
    isLoading.value = true
    try {
      // TODO: Implement stats endpoint
      // stats.value = await restaurantService.getStats(restaurantId)
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const fetchOrders = async (restaurantId: string, page: number = 1, limit: number = 10) => {
    isOrdersLoading.value = true
    try {
      const response = await restaurantService.getOrders(restaurantId, page, limit)
      orders.value = response.orders
      ordersPagination.value = {
        total: response.total,
        page: response.page,
        limit: response.limit,
        totalPages: response.totalPages,
      }
    } catch (error) {
      console.error("Error fetching orders:", error)
      throw error
    } finally {
      isOrdersLoading.value = false
    }
  }

  const fetchActiveOrders = async (restaurantId: string) => {
    isActiveOrdersLoading.value = true
    try {
      activeOrders.value = await restaurantService.getActiveOrders(restaurantId)
    } catch (error) {
      throw error
    } finally {
      isActiveOrdersLoading.value = false
    }
  }

  const fetchMenus = async (restaurantId: string) => {
    isLoading.value = true
    try {
      menus.value = await restaurantService.getMenus(restaurantId)
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const createMenu = async (data: CreateMenuRequest) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      const newMenu = await restaurantService.createMenu(restaurant.value.id, data)
      menus.value.push(newMenu)
      return newMenu
    } catch (error) {
      throw error
    }
  }

  const updateMenu = async (menuId: string, data: Partial<CreateMenuRequest>) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      const updatedMenu = await restaurantService.updateMenu(restaurant.value.id, menuId, data)
      const index = menus.value.findIndex(menu => menu.id === menuId)
      if (index !== -1) {
        menus.value[index] = updatedMenu
      }
      return updatedMenu
    } catch (error) {
      throw error
    }
  }

  const deleteMenu = async (menuId: string) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      await restaurantService.deleteMenu(restaurant.value.id, menuId)
      menus.value = menus.value.filter(menu => menu.id !== menuId)
    } catch (error) {
      throw error
    }
  }

  const fetchTables = async (restaurantId: string) => {
    isLoading.value = true
    try {
      tables.value = await restaurantService.getTables(restaurantId)
      // Reset and preload QR stats only for superadmin (endpoint is SUPERADMIN-only; avoid 403 for restaurant_owner)
      tableQrStats.value = {}
      const authStore = useAuthStore()
      if (authStore.user?.role === "superadmin") {
        await Promise.all(
          tables.value.map(async table => {
            try {
              const stats = await restaurantService.getTableQrStats(restaurantId, table.id)
              tableQrStats.value[table.id] = stats
            } catch (error) {
              console.warn("Failed to load QR stats for table", table.id, error)
            }
          })
        )
      }
    } catch (error) {
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const updateOrderStatus = async (restaurantId: string, orderId: string, status: string) => {
    try {
      await restaurantService.updateOrderStatus(restaurantId, orderId, status)

      // Update the order status in both stores
      const orderIndex = orders.value.findIndex(order => order.id === orderId)
      if (orderIndex !== -1 && orders.value[orderIndex]) {
        orders.value[orderIndex].status = status as any
      }

      // Remove from activeOrders if order is completed/cancelled
      if (status === "delivered" || status === "cancelled") {
        const activeOrderIndex = activeOrders.value.findIndex(order => order.id === orderId)
        if (activeOrderIndex !== -1) {
          activeOrders.value.splice(activeOrderIndex, 1)
        }
      } else {
        // Update status in activeOrders if order still exists there
        const activeOrderIndex = activeOrders.value.findIndex(order => order.id === orderId)
        if (activeOrderIndex !== -1 && activeOrders.value[activeOrderIndex]) {
          activeOrders.value[activeOrderIndex].status = status as any
        }
      }
    } catch (error) {
      throw error
    }
  }

  const updateOrderItemStatus = async (
    restaurantId: string,
    orderId: string,
    itemId: string,
    status: string
  ) => {
    try {
      await restaurantService.updateOrderItemStatus(restaurantId, orderId, itemId, status)

      // Update the specific item in both orders and activeOrders stores
      const orderIndex = orders.value.findIndex(order => order.id === orderId)
      if (orderIndex !== -1) {
        const order = orders.value[orderIndex]
        if (order) {
          const itemIndex = order.items.findIndex(item => item.id === itemId)
          if (itemIndex !== -1 && order.items[itemIndex]) {
            order.items[itemIndex].status = status as any
          }
        }
      }

      // Update in activeOrders as well
      const activeOrderIndex = activeOrders.value.findIndex(order => order.id === orderId)
      if (activeOrderIndex !== -1) {
        const activeOrder = activeOrders.value[activeOrderIndex]
        if (activeOrder) {
          const itemIndex = activeOrder.items.findIndex(item => item.id === itemId)
          if (itemIndex !== -1 && activeOrder.items[itemIndex]) {
            activeOrder.items[itemIndex].status = status as any
          }
        }
      }
    } catch (error) {
      throw error
    }
  }

  const markWaiterServiceCompleted = async (orderId: string) => {
    try {
      const updatedOrder = await restaurantService.markWaiterServiceCompleted(orderId)

      // Update the order in both stores
      const orderIndex = orders.value.findIndex(order => order.id === orderId)
      if (orderIndex !== -1 && orders.value[orderIndex]) {
        orders.value[orderIndex].waiterCalled = false
        orders.value[orderIndex].waiterCalledAt = undefined
      }

      // Update in activeOrders as well
      const activeOrderIndex = activeOrders.value.findIndex(order => order.id === orderId)
      if (activeOrderIndex !== -1 && activeOrders.value[activeOrderIndex]) {
        activeOrders.value[activeOrderIndex].waiterCalled = false
        activeOrders.value[activeOrderIndex].waiterCalledAt = undefined
      }

      return updatedOrder
    } catch (error) {
      throw error
    }
  }

  const createTable = async (tableData: any) => {
    try {
      console.log("createTable - restaurant.value:", restaurant.value)
      console.log("createTable - restaurant.value?.id:", restaurant.value?.id)

      if (!restaurant.value?.id) {
        throw new Error("Restaurant ID not found")
      }
      const newTable = await restaurantService.createTable(restaurant.value.id, tableData)
      tables.value.push(newTable)
      return newTable
    } catch (error) {
      throw error
    }
  }

  const updateTable = async (tableId: string, tableData: any) => {
    try {
      if (!restaurant.value?.id) {
        throw new Error("Restaurant ID not found")
      }
      const updatedTable = await restaurantService.updateTable(
        restaurant.value.id,
        tableId,
        tableData
      )
      const index = tables.value.findIndex(t => t.id === tableId)
      if (index !== -1) {
        tables.value[index] = updatedTable
      }
      return updatedTable
    } catch (error) {
      throw error
    }
  }

  const deleteTable = async (tableId: string) => {
    try {
      if (!restaurant.value?.id) {
        throw new Error("Restaurant ID not found")
      }
      await restaurantService.deleteTable(restaurant.value.id, tableId)
      tables.value = tables.value.filter(t => t.id !== tableId)
    } catch (error) {
      throw error
    }
  }

  const updateRestaurant = async (restaurantId: string, data: any) => {
    try {
      const updatedRestaurant = await restaurantService.updateRestaurant(restaurantId, data)
      if (restaurant.value?.id === restaurantId) {
        restaurant.value = updatedRestaurant
      }
      return updatedRestaurant
    } catch (error) {
      throw error
    }
  }

  const updateImojeConfig = async (restaurantId: string, data: any) => {
    try {
      const updatedRestaurant = await restaurantService.updateImojeConfig(restaurantId, data)
      if (restaurant.value?.id === restaurantId) {
        restaurant.value = updatedRestaurant
      }
      return updatedRestaurant
    } catch (error) {
      throw error
    }
  }

  const updateRestaurantPackage = async (
    restaurantId: string,
    packageType: "SIMPLE" | "FULL"
  ) => {
    console.log("🔄 [Store] updateRestaurantPackage called:", { restaurantId, packageType })
    try {
      const updatedRestaurant = await restaurantService.updateRestaurant(restaurantId, {
        packageType,
      })
      console.log("✅ [Store] Restaurant updated:", updatedRestaurant)
      // Update in restaurants array if it exists
      const restaurantIndex = restaurants.value.findIndex(r => r.id === restaurantId)
      if (restaurantIndex !== -1) {
        restaurants.value[restaurantIndex] = updatedRestaurant
        console.log("✅ [Store] Updated restaurant in array")
      }
      // Update current restaurant if it matches
      if (restaurant.value?.id === restaurantId) {
        restaurant.value = updatedRestaurant
        console.log("✅ [Store] Updated current restaurant")
      }
      return updatedRestaurant
    } catch (error) {
      console.error("❌ [Store] Error in updateRestaurantPackage:", error)
      throw error
    }
  }

  const updateSmsConfig = async (restaurantId: string, data: { smsEnabled?: boolean }) => {
    try {
      const updatedRestaurant = await restaurantService.updateSmsConfig(restaurantId, data)
      if (restaurant.value?.id === restaurantId) {
        restaurant.value = updatedRestaurant
      }
      return updatedRestaurant
    } catch (error) {
      throw error
    }
  }

  const updateSmsLimit = async (restaurantId: string, smsLimit: number | null) => {
    try {
      const updatedRestaurant = await restaurantService.updateSmsLimit(restaurantId, smsLimit)
      if (restaurant.value?.id === restaurantId) {
        restaurant.value = updatedRestaurant
      }
      return updatedRestaurant
    } catch (error) {
      throw error
    }
  }

  const sendOrderReadySms = async (orderId: string) => {
    try {
      await restaurantService.sendOrderReadySms(orderId)
    } catch (error) {
      throw error
    }
  }

  // Section management
  const fetchSections = async (menuId: string) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.getMenuSections(restaurant.value.id, menuId)
    } catch (error) {
      throw error
    }
  }

  const createSection = async (menuId: string, data: CreateSectionRequest) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.createSection(restaurant.value.id, menuId, data)
    } catch (error) {
      throw error
    }
  }

  const updateSection = async (
    menuId: string,
    sectionId: string,
    data: Partial<CreateSectionRequest>
  ) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.updateSection(restaurant.value.id, menuId, sectionId, data)
    } catch (error) {
      throw error
    }
  }

  const deleteSection = async (menuId: string, sectionId: string) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.deleteSection(restaurant.value.id, menuId, sectionId)
    } catch (error) {
      throw error
    }
  }

  // Product management
  const fetchProducts = async (menuId: string, sectionId: string) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.getSectionProducts(restaurant.value.id, menuId, sectionId)
    } catch (error) {
      throw error
    }
  }

  const createProduct = async (menuId: string, sectionId: string, data: CreateProductRequest) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.createProduct(restaurant.value.id, menuId, sectionId, data)
    } catch (error) {
      throw error
    }
  }

  const updateProduct = async (
    menuId: string,
    sectionId: string,
    productId: string,
    data: Partial<CreateProductRequest>
  ) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.updateProduct(
        restaurant.value.id,
        menuId,
        sectionId,
        productId,
        data
      )
    } catch (error) {
      throw error
    }
  }

  const deleteProduct = async (menuId: string, sectionId: string, productId: string) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.deleteProduct(
        restaurant.value.id,
        menuId,
        sectionId,
        productId
      )
    } catch (error) {
      throw error
    }
  }

  // Section Product Link management
  const attachProductToSection = async (
    menuId: string,
    sectionId: string,
    productId: string,
    sortOrder?: number
  ) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.attachProductToSection(
        restaurant.value.id,
        menuId,
        sectionId,
        productId,
        sortOrder
      )
    } catch (error) {
      throw error
    }
  }

  const detachProductFromSection = async (menuId: string, sectionId: string, productId: string) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.detachProductFromSection(
        restaurant.value.id,
        menuId,
        sectionId,
        productId
      )
    } catch (error) {
      throw error
    }
  }

  const reorderSectionProducts = async (
    menuId: string,
    sectionId: string,
    items: Array<{ productId: string; sortOrder: number }>
  ) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.reorderSectionProducts(
        restaurant.value.id,
        menuId,
        sectionId,
        items
      )
    } catch (error) {
      throw error
    }
  }

  const getSectionProductsWithLinks = async (menuId: string, sectionId: string) => {
    if (!restaurant.value) {
      throw new Error("Restaurant not found")
    }
    try {
      return await restaurantService.getSectionProductsWithLinks(
        restaurant.value.id,
        menuId,
        sectionId
      )
    } catch (error) {
      throw error
    }
  }

  return {
    restaurant,
    restaurants,
    allOrders,
    stats,
    orders,
    activeOrders,
    ordersPagination,
    menus,
    tables,
    carts,
    isLoading,
    isOrdersLoading,
    isActiveOrdersLoading,
    tableQrStats,
    completedOrders,
    fetchRestaurant,
    fetchAllRestaurants,
    fetchAllRestaurantsAdmin,
    fetchAllOrders,
    setActiveRestaurant,
    clearRestaurantData,
    initializeUserRestaurant,
    fetchStats,
    fetchOrders,
    fetchActiveOrders,
    fetchMenus,
    createMenu,
    updateMenu,
    deleteMenu,
    fetchTables,
    updateOrderStatus,
    updateOrderItemStatus,
    markWaiterServiceCompleted,
    createTable,
    updateTable,
    deleteTable,
    updateRestaurant,
    updateImojeConfig,
    updateRestaurantPackage,
    updateSmsConfig,
    updateSmsLimit,
    sendOrderReadySms,
    fetchSections,
    createSection,
    updateSection,
    deleteSection,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    attachProductToSection,
    detachProductFromSection,
    reorderSectionProducts,
    getSectionProductsWithLinks,
    // Independent product management
    fetchAllProducts: async (search?: string, includeArchived = false) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        isLoading.value = true
        return await restaurantService.getAllProducts(restaurant.value.id, search, includeArchived)
      } catch (error) {
        throw error
      } finally {
        isLoading.value = false
      }
    },
    fetchAllProductsPaginated: async (
      page: number = 1,
      limit: number = 20,
      search?: string,
      includeArchived = false
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        isLoading.value = true
        return await restaurantService.getAllProductsPaginated(
          restaurant.value.id,
          page,
          limit,
          search,
          includeArchived
        )
      } catch (error) {
        throw error
      } finally {
        isLoading.value = false
      }
    },
    fetchProduct: async (productId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.getProduct(restaurant.value.id, productId)
      } catch (error) {
        throw error
      }
    },
    createProductIndependent: async (data: any) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.createProductIndependent(restaurant.value.id, data)
      } catch (error) {
        throw error
      }
    },
    updateProductIndependent: async (productId: string, data: any) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.updateProductIndependent(
          restaurant.value.id,
          productId,
          data
        )
      } catch (error) {
        throw error
      }
    },
    deleteProductIndependent: async (productId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.deleteProductIndependent(restaurant.value.id, productId)
      } catch (error) {
        throw error
      }
    },
    restoreProductIndependent: async (productId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.restoreProductIndependent(restaurant.value.id, productId)
      } catch (error) {
        throw error
      }
    },
    createVariantIndependent: async (productId: string, data: any) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.createVariantIndependent(
          restaurant.value.id,
          productId,
          data
        )
      } catch (error) {
        throw error
      }
    },
    updateVariantIndependent: async (productId: string, variantId: string, data: any) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.updateVariantIndependent(
          restaurant.value.id,
          productId,
          variantId,
          data
        )
      } catch (error) {
        throw error
      }
    },
    deleteVariantIndependent: async (productId: string, variantId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.deleteVariantIndependent(
          restaurant.value.id,
          productId,
          variantId
        )
      } catch (error) {
        throw error
      }
    },
    createAddonIndependent: async (productId: string, data: any) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.createAddonIndependent(restaurant.value.id, productId, data)
      } catch (error) {
        throw error
      }
    },
    updateAddonIndependent: async (productId: string, addonId: string, data: any) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.updateAddonIndependent(
          restaurant.value.id,
          productId,
          addonId,
          data
        )
      } catch (error) {
        throw error
      }
    },
    deleteAddonIndependent: async (productId: string, addonId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.deleteAddonIndependent(
          restaurant.value.id,
          productId,
          addonId
        )
      } catch (error) {
        throw error
      }
    },

    // Product Translations
    createProductTranslation: async (
      productId: string,
      data: { language: string; name: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.createProductTranslation(
          restaurant.value.id,
          productId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    updateProductTranslation: async (
      productId: string,
      translationId: string,
      data: { language?: string; name?: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.updateProductTranslation(
          restaurant.value.id,
          productId,
          translationId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    deleteProductTranslation: async (productId: string, translationId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.deleteProductTranslation(
          restaurant.value.id,
          productId,
          translationId
        )
      } catch (error) {
        throw error
      }
    },

    // Section Translations
    createSectionTranslation: async (
      menuId: string,
      sectionId: string,
      data: { language: string; name: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.createSectionTranslation(
          restaurant.value.id,
          menuId,
          sectionId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    updateSectionTranslation: async (
      menuId: string,
      sectionId: string,
      translationId: string,
      data: { name?: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.updateSectionTranslation(
          restaurant.value.id,
          menuId,
          sectionId,
          translationId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    // Variant Translations
    createVariantTranslation: async (
      productId: string,
      variantId: string,
      data: { language: string; name: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.createVariantTranslation(
          restaurant.value.id,
          productId,
          variantId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    updateVariantTranslation: async (
      productId: string,
      variantId: string,
      translationId: string,
      data: { language?: string; name?: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.updateVariantTranslation(
          restaurant.value.id,
          productId,
          variantId,
          translationId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    deleteVariantTranslation: async (
      productId: string,
      variantId: string,
      translationId: string
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.deleteVariantTranslation(
          restaurant.value.id,
          productId,
          variantId,
          translationId
        )
      } catch (error) {
        throw error
      }
    },

    getProductById: async (productId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.getProduct(restaurant.value.id, productId)
      } catch (error) {
        throw error
      }
    },

    // Product Addon Translation actions
    createAddonTranslation: async (
      productId: string,
      addonId: string,
      data: { language: string; name: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.createAddonTranslation(
          restaurant.value.id,
          productId,
          addonId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    updateAddonTranslation: async (
      productId: string,
      addonId: string,
      translationId: string,
      data: { language: string; name: string; description?: string }
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.updateAddonTranslation(
          restaurant.value.id,
          productId,
          addonId,
          translationId,
          data
        )
      } catch (error) {
        throw error
      }
    },

    deleteAddonTranslation: async (productId: string, addonId: string, translationId: string) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.deleteAddonTranslation(
          restaurant.value.id,
          productId,
          addonId,
          translationId
        )
      } catch (error) {
        throw error
      }
    },

    // Cart actions
    fetchCarts: async (restaurantId: string) => {
      try {
        const cartsData = await restaurantService.getRestaurantCarts(restaurantId)
        carts.value = cartsData
        return cartsData
      } catch (error) {
        throw error
      }
    },

    // Reorder methods
    reorderVariants: async (
      productId: string,
      reorderData: { variantId: string; sortOrder: number }[]
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.reorderVariants(restaurant.value.id, productId, reorderData)
      } catch (error) {
        throw error
      }
    },

    reorderAddons: async (
      productId: string,
      reorderData: { addonId: string; sortOrder: number }[]
    ) => {
      if (!restaurant.value) {
        throw new Error("Restaurant not found")
      }
      try {
        return await restaurantService.reorderAddons(restaurant.value.id, productId, reorderData)
      } catch (error) {
        throw error
      }
    },
  }
})
